import I18n from 'i18n-js';
import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import { LANG_STORAGE } from 'src/constants/localStorage';
import { LANG, DEFAULT_LANG } from '../constants/lang';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import 'dayjs/locale/th';
import 'dayjs/locale/id';
import 'dayjs/locale/vi';
import 'dayjs/locale/ms';
import { MetricType } from '@services/insight/type';

export const getLang = (): LANG => {
  const lang = window.localStorage.getItem(LANG_STORAGE);
  // Convert the string to uppercase to match the enum format
  const upperLang = lang?.toUpperCase() as LANG;
  // Check if the lang exists in the LANG enum
  if (upperLang && Object.values(LANG).includes(upperLang)) {
    return upperLang;
  }
  return DEFAULT_LANG;
};

export const loadLang = async (lang: LANG) => {
  const localePath = lang.toLowerCase();
  const locales = [
    import(`@assets/lang/${localePath}.json`),
    // Add additional namespaces as needed
  ];
  const [localResult] = await Promise.allSettled(locales);

  if (localResult.status === 'fulfilled') {
    i18next.addResources(lang, 'translation', localResult.value);
    I18n.translations[lang] = localResult.value;
  }
};

export const setLang = async (lang: LANG) => {
  await loadLang(lang);
  I18n.locale = lang;
  await i18next.changeLanguage(lang);

  // Set up dayjs locale if needed
  const dayjsLangMap: { [key in LANG]: string } = {
    [LANG.EN]: 'en',
    [LANG.ID]: 'id',
    [LANG.VN]: 'vi',
    [LANG.CN]: 'zh-cn',
    [LANG.TH]: 'th',
    [LANG.MY]: 'ms',
  };
  dayjs.locale(dayjsLangMap[lang]);
};

export const init = async (): Promise<void> => {
  const lang = getLang();
  const options = {
    lng: lang,
    ns: ['translation'],
    defaultNS: 'translation',
    fallbackLng: DEFAULT_LANG,
    debug: process.env.NODE_ENV === 'development',
    interpolation: {
      escapeValue: false,
    },
  };

  await i18next.use(initReactI18next).init(options);
  if (lang !== DEFAULT_LANG) {
    await loadLang(DEFAULT_LANG);
  }
  await loadLang(lang);
  await setLang(lang);
};

export const MetricTranslationKeyMap: Record<MetricType, string> = {
  churnRate: 'dna_crm_label_churnRate',
  totalCustomers: 'dna_customer_insights_heading_total_customers',
  totalOrders: 'dna_crm_label_totalTransactions',
  averageOrderValue: 'dna_crm_label_averageTransactionValue',
  averageOrderInterval: 'dna_crm_label_averageTransactionInterval',
};

export const FilterTranslationKeyMap: Record<string, string> = {
  'Past 30 days': 'mex-insightsv2-003-007-dropdown',
  'Past 60 days': 'dna_crm_body_past60Days',
  'Past 90 days': 'mex-insightsv2-003-009-dropdown',
  'All channels': 'dna_crm_body_allChannels',
  'Delivery channel': 'dna_crm_body_deliveryChannel',
  'Dine out channel': 'dna_crm_body_dineoutChannel',
  'Loyalty members': 'dna_crm_label_loyaltyMembers',
  'All type of customers': 'dna_crm_filter_allCustomerTypes',
  'All customers': 'dna_crm_label_allCustomers',
  'New customers': 'dna_customer_insights_heading_new_customer',
  'Frequent customers': 'dna_crm_header_frequentCustomers',
  'High spend customers': 'dna_crm_header_highSpendCustomers',
  'Churned customers': 'dna_crm_header_churnedCustomers',
};

// Handle both <Bold> and <Colour> tags with proper nesting support
export const formatByStyle = (text: string, isColorBold: boolean = false) => {
  // Check if there are any tags to process
  if (!/<(Bold|Colour)/.test(text)) {
    return text;
  }

  const result: JSX.Element[] = [];
  let currentIndex = 0;
  let keyCounter = 0;
  let isBoldTagActive = false;
  let activeColor: string | null = null;

  // Regular expression to match both Bold and Colour tags
  const tagRegex = /<(Bold|Colour(#[0-9A-Fa-f]{6})?)>/g;
  let match;

  match = tagRegex.exec(text);
  while (match !== null) {
    const [fullMatch, tagType, colorCode] = match;
    const matchStart = match.index;

    // Add text before this tag if there's any
    if (matchStart > currentIndex) {
      const textContent = text.slice(currentIndex, matchStart);
      if (textContent) {
        const isBold = isBoldTagActive || (!!activeColor && isColorBold);
        const className = isBold ? 'font-semibold' : '';
        const style = activeColor ? { color: activeColor } : {};

        result.push(
          <span key={`text-${keyCounter}`} className={className} style={style}>
            {textContent}
          </span>,
        );
        keyCounter += 1;
      }
    }

    // Update state based on tag type
    if (tagType === 'Bold') {
      isBoldTagActive = !isBoldTagActive; // Toggle bold state
    } else if (tagType.startsWith('Colour')) {
      if (colorCode) {
        // Opening color tag with color code
        activeColor = colorCode;
      } else {
        // Closing color tag (just "Colour" without color code)
        activeColor = null;
      }
    }

    currentIndex = matchStart + fullMatch.length;
    match = tagRegex.exec(text);
  }

  // Add remaining text after the last tag
  if (currentIndex < text.length) {
    const textContent = text.slice(currentIndex);
    if (textContent) {
      const isBold = isBoldTagActive || (!!activeColor && isColorBold);
      const className = isBold ? 'font-semibold' : '';
      const style = activeColor ? { color: activeColor } : {};

      result.push(
        <span key={`text-${keyCounter}`} className={className} style={style}>
          {textContent}
        </span>,
      );
      keyCounter += 1;
    }
  }

  return result.length > 0 ? <>{result}</> : text;
};
